import { useEffect, useState, type FormEvent } from 'react';
import {
  fetchAdminChangelog,
  publishChangelog,
  testDiscordIdentity,
  updateDiscordBot,
  type AdminChangelogRelease,
  type ChangelogMode,
  type DiscordBotView,
} from '../../api/changelog';
import { ca } from '../../i18n/ca';
import { discordRefusalText, errorText, fmtWhen } from './shared';

const MODES: ChangelogMode[] = ['RELEASE', 'ENTRY', 'MANUAL'];

/**
 * The bot itself: one token, one channel per kind of notice, and a way to ask
 * Discord who the token belongs to without posting anything anywhere.
 *
 * The token is written and never read back, like the server passwords beside
 * it: an empty field means "keep the stored one".
 */
export function DiscordBotCard({
  view,
  onSaved,
  onError,
}: {
  view: DiscordBotView;
  onSaved: (v: DiscordBotView) => void;
  onError: (text: string | null) => void;
}) {
  const t = ca.discordBot;
  const [token, setToken] = useState('');
  const [enabled, setEnabled] = useState(view.settings.botEnabled);
  const [sessions, setSessions] = useState(view.settings.sessionsChannelId);
  const [votes, setVotes] = useState(view.settings.votesChannelId);
  const [changelog, setChangelog] = useState(view.settings.changelogChannelId);
  const [proves, setProves] = useState(view.settings.provesChannelId ?? '');
  const [publicKey, setPublicKey] = useState(view.settings.publicKey ?? '');
  const [saved, setSaved] = useState(false);
  const [who, setWho] = useState<{ text: string; ok: boolean } | null>(null);

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      const next = await updateDiscordBot({
        botEnabled: enabled,
        ...(token ? { botToken: token } : {}),
        sessionsChannelId: sessions,
        votesChannelId: votes,
        changelogChannelId: changelog,
        provesChannelId: proves,
        publicKey,
      });
      setToken('');
      setSaved(true);
      onSaved(next);
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  function askWhoItIs() {
    testDiscordIdentity()
      .then((r) =>
        setWho(
          !r.configured
            ? { text: t.identityUnset, ok: false }
            : r.name
              ? { text: t.identityOk(r.name), ok: true }
              : { text: t.identityBad, ok: false },
        ),
      )
      .catch(() => setWho({ text: t.identityBad, ok: false }));
  }

  return (
    <form className="card form" onSubmit={save} aria-label={t.card}>
      <h2>
        <i className="bi bi-robot" aria-hidden="true" /> {t.card}
      </h2>
      <p className="muted small">{t.intro}</p>
      <label className="check">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        {t.enabled}
      </label>
      <label>
        {t.token}
        <input
          name="discordBotToken"
          type="password"
          autoComplete="new-password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <span className="hint">{t.tokenHint}</span>
        <span className="hint">{view.settings.botTokenSet ? t.tokenSet : t.tokenUnset}</span>
      </label>
      <fieldset className="form">
        <legend>{t.channels}</legend>
        <ChannelField
          label={t.channelChangelog}
          name="changelogChannelId"
          value={changelog}
          transport={view.transports.changelog}
          onChange={setChangelog}
        />
        <ChannelField
          label={t.channelSessions}
          name="sessionsChannelId"
          value={sessions}
          transport={view.transports.sessions}
          onChange={setSessions}
        />
        <ChannelField
          label={t.channelVotes}
          name="votesChannelId"
          value={votes}
          transport={view.transports.votes}
          onChange={setVotes}
        />
        <ChannelField
          label={t.channelProves}
          name="provesChannelId"
          value={proves}
          transport={view.transports.proves ?? 'none'}
          onChange={setProves}
        />
        <p className="hint">{t.channelHint}</p>
      </fieldset>
      <label>
        {t.publicKey}
        <input
          name="discordPublicKey"
          value={publicKey}
          onChange={(e) => setPublicKey(e.target.value)}
          autoComplete="off"
        />
        <span className="hint">{t.publicKeyHint}</span>
      </label>
      <p className="muted small">{t.permissions}</p>
      <div className="row actions">
        <button type="submit" className="primary">
          {ca.saveSettings}
        </button>
        <button type="button" onClick={askWhoItIs}>
          <i className="bi bi-person-badge" aria-hidden="true" /> {t.identity}
        </button>
        {saved && (
          <span className="muted small" role="status">
            {ca.settingsSaved}
          </span>
        )}
      </div>
      {who && (
        <p className={`notice ${who.ok ? 'ok' : 'bad'}`} role="status">
          {who.text}
        </p>
      )}
    </form>
  );
}

function ChannelField({
  label,
  name,
  value,
  transport,
  onChange,
}: {
  label: string;
  name: string;
  value: string;
  transport: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <label>
      <span className="row between">
        {label}
        <span className="chip">{ca.discordBot.transports[transport ?? 'none']}</span>
      </span>
      <input
        name={name}
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/**
 * The changelog's own switch, its schedule, and one button per version.
 *
 * The button is here because the automatic half is deliberately narrow: it
 * announces a version once, the first time the platform comes up carrying it.
 * A channel somebody cleared, or a release that shipped before the channel
 * existed, is a person's call.
 */
export function ChangelogCard({
  view,
  onSaved,
  onError,
}: {
  view: DiscordBotView;
  onSaved: (v: DiscordBotView) => void;
  onError: (text: string | null) => void;
}) {
  const t = ca.changelogAdmin;
  const [enabled, setEnabled] = useState(view.settings.changelogEnabled);
  const [mode, setMode] = useState<ChangelogMode>(view.settings.changelogMode);
  const [role, setRole] = useState(view.settings.changelogMentionRoleId);
  const [saved, setSaved] = useState(false);
  const [releases, setReleases] = useState<AdminChangelogRelease[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    fetchAdminChangelog()
      .then(setReleases)
      .catch(() => setReleases([]));
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      const next = await updateDiscordBot({
        changelogEnabled: enabled,
        changelogMode: mode,
        changelogMentionRoleId: role,
      });
      setSaved(true);
      onSaved(next);
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  async function publish(itemKey: string) {
    setBusy(itemKey);
    try {
      const answer = await publishChangelog(itemKey);
      // Discord's own reason when there is one: it names the single thing to
      // change, where the generic sentence names all three.
      const refusal = discordRefusalText(answer.discord);
      setResult(
        answer.sent
          ? { text: t.sent, ok: true }
          : { text: refusal ?? t.reasons[answer.reason] ?? t.reasons.refused, ok: false },
      );
      if (answer.sent) setReleases(await fetchAdminChangelog());
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <form className="card form" onSubmit={save} aria-label={t.card}>
      <h2>
        <i className="bi bi-megaphone" aria-hidden="true" /> {t.card}
      </h2>
      <p className="muted small">{t.intro}</p>
      <label className="check">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        {t.enabled}
      </label>
      <label>
        {t.mode}
        <select
          name="changelogMode"
          value={mode}
          onChange={(e) => setMode(e.target.value as ChangelogMode)}
        >
          {MODES.map((m) => (
            <option key={m} value={m}>
              {t.modes[m]}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t.mention}
        <input
          name="changelogMentionRoleId"
          inputMode="numeric"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        />
        <span className="hint">{t.mentionHint}</span>
      </label>
      <div className="row actions">
        <button type="submit" className="primary">
          {ca.saveSettings}
        </button>
        {saved && (
          <span className="muted small" role="status">
            {ca.settingsSaved}
          </span>
        )}
      </div>
      {result && (
        <p className={`notice ${result.ok ? 'ok' : 'bad'}`} role="status">
          {result.text}
        </p>
      )}
      <ul className="changelog-releases">
        {releases.map((release) => (
          <li key={release.version}>
            <span className="row between">
              <strong>
                {release.unreleased
                  ? ca.changelogPage.unreleased
                  : ca.changelogPage.version(release.version)}
              </strong>
              <span className="muted small">
                {ca.changelogAdmin.entriesCount(release.entries.length)}
              </span>
            </span>
            <span className="row between">
              <span className="muted small">{state(release)}</span>
              {!release.unreleased && (
                <button
                  type="button"
                  className="ghost small"
                  disabled={busy === release.version}
                  onClick={() => publish(release.version)}
                >
                  <i className="bi bi-send" aria-hidden="true" />{' '}
                  {release.publishedAt ? t.republish : t.publish}
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </form>
  );

  function state(release: AdminChangelogRelease): string {
    if (release.unreleased) return t.unreleasedNever;
    if (release.publishedAt) return t.publishedAt(fmtWhen(release.publishedAt));
    return release.seeded ? t.seeded : t.never;
  }
}
